import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EditableEntryField } from '../editable-field-config'

export interface EntryFieldValidationError {
  message: string
  autoDismiss?: boolean // If true, error will auto-close after delay
}

export interface FieldEditState {
  parentId: string
  entryId: string
  fieldKey: EditableEntryField
  originalValue: string
  pendingValue: string
  isEditing: boolean
  isSaving: boolean
  validationError: EntryFieldValidationError | null
}

interface EditState {
  activeEdit: FieldEditState | null // Only one field can be edited at a time
}

// Identifies which field a save outcome belongs to. Captured by the business listener at dispatch
// time (not read back from state later) so the entries-edit analytics listener
// (src/analytics/store/listeners/medsummary-entry-edit-analytics.listener.ts) never has to guess
// whether `activeEdit` still refers to the save that just completed.
interface FieldSaveContext {
  parentId: string
  entryId: string
  fieldKey: EditableEntryField
}

const initialState: EditState = {
  activeEdit: null,
}

export const medSummaryEntryEditSlice = createSlice({
  name: 'medSummaryEntryEditState',
  initialState,
  reducers: {
    // ============== Listener Actions ============== >
    saveFieldEdit: (_state, _action: PayloadAction<{ autoCancelOnError: boolean }>) => {
      // Handled by the listener middleware
    },

    // No-op for this reducer — consumed by the entries-edit analytics listener to fire
    // MedSummaryAssetEntryFieldSaved. A dedicated action (rather than inferring success from the
    // generic MedSummaryEntriesActions.updateEntry) keeps the analytics signal explicit and
    // self-contained.
    saveFieldSucceeded: (_state, _action: PayloadAction<FieldSaveContext>) => {},

    // No-op for this reducer — consumed by the entries-edit analytics listener to fire
    // MedSummaryAssetEntryFieldSaveFailed with the mapped error shape.
    saveFieldFailed: (_state, _action: PayloadAction<FieldSaveContext & { errorType: string; statusCode?: number }>) => {},

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    // Enter edit mode for a field
    startEditField: (state, action: PayloadAction<{ parentId: string; entryId: string; fieldKey: EditableEntryField; originalValue: string }>) => {
      const { parentId, entryId, fieldKey, originalValue } = action.payload

      // Start new edit (automatically replaces any existing edit)
      state.activeEdit = {
        parentId,
        entryId,
        fieldKey,
        originalValue,
        pendingValue: originalValue,
        isEditing: true,
        isSaving: false,
        validationError: null,
      }
    },

    // Update pending value as user types
    updatePendingValue: (state, action: PayloadAction<{ pendingValue: string }>) => {
      if (state.activeEdit) {
        state.activeEdit.pendingValue = action.payload.pendingValue
        // Clear validation error when user changes value
        state.activeEdit.validationError = null
      }
    },

    // Set client-side validation error
    setValidationError: (state, action: PayloadAction<{ message: string; autoDismiss: boolean }>) => {
      if (state.activeEdit) {
        state.activeEdit.validationError = {
          message: action.payload.message,
          autoDismiss: action.payload.autoDismiss,
        }
        state.activeEdit.isSaving = false
      }
    },

    // Cancel edit mode
    cancelEditField: (state) => {
      state.activeEdit = null
    },

    // Start save
    startSaveField: (state) => {
      if (state.activeEdit) {
        state.activeEdit.isSaving = true
      }
    },

    // Complete save (clear edit state)
    completeSaveField: (state) => {
      state.activeEdit = null
    },
  },
})

export const MedSummaryEntryEditActions = medSummaryEntryEditSlice.actions
export default medSummaryEntryEditSlice.reducer
